import React, { useState } from "react";
import { useIntl } from "react-intl";
import axios from "axios";
import { KeenIcon } from "@/components";
import { enqueueSnackbar } from "notistack";

interface HardCriteriaComparisonProps {
    hardCriteriaResults: { [key: string]: boolean };
    match_p_id: string;
}

interface Criterion {
    name: string;
    label: string;
    value: boolean;
}

export const HardCriteriaComparison = ({
                                           hardCriteriaResults,
                                           match_p_id,
                                       }: HardCriteriaComparisonProps) => {
    const intl = useIntl();

    // map initial props → local state
    const mapToState = (): Criterion[] =>
        Object.entries(hardCriteriaResults).map(([key, val]) => ({
            name: key,
            label: intl.formatMessage({ id: key }),
            value: val,
        }));

    const [details, setDetails] = useState<Criterion[]>(mapToState());
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    const handleToggle = (idx: number) => {
        const copy = [...details];
        copy[idx].value = !copy[idx].value;
        setDetails(copy);
    };

    const handleEdit = () => setIsEditing(true);
    const handleCancel = () => {
        setDetails(mapToState());
        setIsEditing(false);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            // build the flat map first
            const flatMap = details.reduce<Record<string, boolean>>(
                (acc, { name, value }) => ({ ...acc, [name]: value }),
                {}
            );
            // now wrap it under "hard_criteria"
            const payload = { hard_criteria: flatMap };

            await axios.put(
                `${API_URL}/match/${match_p_id}/hard-criteria`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            enqueueSnackbar("Hard criteria updated", { variant: "solid", state: "success" });
            setIsEditing(false);
        } catch (err: any) {
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card">
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">Hard Criteria</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEdit}>
                        <KeenIcon icon="notepad-edit" /> Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            <KeenIcon icon="file-up" /> {loading ? "Saving..." : "Save"}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            <KeenIcon icon="tablet-delete" /> Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">
                {details.map(({ name, label, value }, idx) => (
                    <div
                        key={name}
                        className="flex justify-between items-center mb-2 h-6"
                    >
                        <span className="text-sm font-medium">{label}</span>

                        {!isEditing ? (
                            <span
                                className={`badge badge-sm ${
                                    value ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"
                                }`}
                            >
                {value ? "Passed" : "Failed"}
              </span>
                        ) : (
                            <label className="switch switch-sm">
                                <input
                                    type="checkbox"
                                    checked={value}
                                    onChange={() => handleToggle(idx)}
                                />
                                <span className="slider round"></span>
                            </label>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
